import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createDetection,
  deleteDetection,
  fetchDetectionDetail,
  fetchDetectionList,
  fetchSiteRoad,
  updateDetection,
} from '@/domains/scenario/apis/event-management';
import type {
  CreateDetectionRequest,
  CreateDetectionResponse,
  DetectionDetailResponse,
  DetectionListResponse,
  SiteRoadResponse,
  UpdateDetectionRequest,
} from '@/domains/scenario/types/event-management.types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

interface DetectionInputForm {
  detection_id: string;
  name: string;
  site_id: string;
  site_name: string;
  road_name: string;
  road_id: string;
  type: string;
  type_value: string;
  polygons: string;
}

// Hook parameter types
interface UseEventManagementParams {
  detectionId?: string;
  queryParams?: string;
  siteRoadParams?: string;
  onCreateSuccess?: (data: { detection_id: number }) => void;
  onUpdateSuccess?: (data: DetectionDetailResponse) => void;
  onDeleteSuccess?: () => void;
}

// Hook return type
interface UseEventManagementReturn {
  detectionListData: DetectionListResponse | undefined;
  detailDetectionData: DetectionDetailResponse | undefined;
  createDetection: (data: CreateDetectionRequest) => void;
  updateDetection: (data: UpdateDetectionRequest) => void;
  deleteDetection: (id: string) => void;
  siteRoad: SiteRoadResponse | undefined;
}

const useEventManagement = ({
  detectionId = '',
  queryParams = '',
  siteRoadParams = '',
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  onDeleteSuccess = () => {},
}: UseEventManagementParams): UseEventManagementReturn => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Query to fetch detection list
  const { data: detectionListData } = useQuery({
    queryKey: ['detectionListData', queryParams],
    queryFn: () => fetchDetectionList(queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  // Query to fetch detailed detection data
  const { data: detailDetectionData } = useQuery({
    queryKey: ['detailDetectionData', detectionId],
    queryFn: () => fetchDetectionDetail(detectionId),
    retry: false,
    enabled: !!detectionId,
  });

  // Mutation: Create Detection
  const createDetectionMutation = useMutation<
    CreateDetectionResponse,
    Error,
    CreateDetectionRequest
  >({
    mutationFn: (detectionData: CreateDetectionRequest) =>
      createDetection(detectionData) as Promise<CreateDetectionResponse>,
    onSuccess: (responseData: CreateDetectionResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['detectionListData', queryParams],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (err: Error) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // Mutation: Update Detection
  const updateDetectionMutation = useMutation<
    DetectionDetailResponse,
    Error,
    UpdateDetectionRequest
  >({
    mutationFn: (detectionData: UpdateDetectionRequest) =>
      updateDetection(detectionId, detectionData) as Promise<DetectionDetailResponse>,
    onSuccess: (responseData: DetectionDetailResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['detectionListData', queryParams],
          });
          onUpdateSuccess(responseData);
        },
      });
    },
    onError: (err: Error) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // Mutation: Delete Detection
  const deleteDetectionMutation = useMutation({
    mutationFn: (detectionId: string) => deleteDetection(detectionId),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 삭제되었습니다.'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({
            queryKey: ['detectionListData', queryParams],
          });
        },
      });
    },
    onError: (err: Error) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // Query to site road list
  const { data: siteRoad } = useQuery({
    queryKey: ['siteRoad', siteRoadParams],
    queryFn: () => fetchSiteRoad(siteRoadParams),
    staleTime: 1000 * 60 * 1,
  });

  return {
    detectionListData,
    detailDetectionData,
    createDetection: createDetectionMutation.mutate,
    updateDetection: updateDetectionMutation.mutate,
    deleteDetection: deleteDetectionMutation.mutate,
    siteRoad,
  };
};

export default useEventManagement;
